import React, { useEffect, useMemo, useCallback } from 'react'
import { map, values, replace } from '@neo/ramda-extra'
import {
  Title,
  SectionTitle as SecTitle,
  Subtitle,
} from 'features/broker/applications/components/typography'
import { useWizard, Wizard } from '@vega/formik-wizard'
import { useFormikContext } from 'formik'
import { useHistory } from 'react-router-dom'
import { Expenses } from './Expense'
import { useDispatch, useSelector } from 'react-redux'
import {
  createExpensesAndLiabilities,
  resetForm,
  updateFormProgress,
  selectProgressStepStatus,
} from 'modules/application'
import { FuturePlan } from './FuturePlan'
import { FormProgress } from '../../../components/FormProgress/FormProgress'
import { PersonalLoan, CreditCard } from './Liabilities'
import { InfoSidebar } from './InfoSidebar'
import { BottomNavigation } from './BottomNavigation'
import { validationSchema } from './validationSchema'
import { routes } from 'routes'
import { styled, s } from '@vega/styled'
import { hasNoErrors } from '../utils'
import { markProgressStepAsVisited } from 'features/broker/applications/components/FormProgress/utils'
import * as CONSTANTS from '@neo/constants'

const {
  FORM_PROGRESS: {
    STEP_STATUS: { COMPLETED },
    STEP_NAMES: { EXPENSES_AND_LIABILITIES },
  },
} = CONSTANTS

const Root = styled.div(s('flex-1 flex items-center w-full h-full relative'))
const ProgressContainer = styled.div(s('bg-white h-full'))

const Core = styled.div(s('flex flex-column w-full h-full'))
const Content = styled.div(
  s('flex-1 flex justify-center w-full pt-6', {
    paddingRight: 200,
    overflowY: 'scroll',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      width: 0,
      height: 0,
    },
  })
)
const FormContainer = styled.div(s('flex flex-column pt-1'), {
  width: 652,
  '> :last-child': s('pb-12'),
})
const InfoContainer = styled.div(s('relative', { pointerEvents: 'none' }))
const NavigationContainer = styled.div(s('relative zIndex-2'))

const SectionTitle = styled(SecTitle)(s('mt-5'))

const extractFuturePlans = (financials) => {
  const allFinancialInfo = values(financials)

  return map((financialInfo) => {
    return financialInfo?.futurePlan
  }, allFinancialInfo)
}

function YourExpensesAndLiabilitiesStep({
  stepId,
  applicantId,
  applicantPathId,
  loanApplicationId,
}) {
  const parentPath = `financials.${applicantPathId}`
  const history = useHistory()
  const dispatch = useDispatch()
  const { values: formikValues, errors } = useFormikContext()

  const saveAndContinue = (applicantId) => (formikValues) => {
    const formValues = formikValues?.financials[applicantPathId]
    const allFuturePlans = extractFuturePlans(formikValues?.financials)
    dispatch(createExpensesAndLiabilities({ formValues, allFuturePlans, applicantId }))
    dispatch(
      updateFormProgress({
        step: EXPENSES_AND_LIABILITIES,
        status: COMPLETED,
        applicantId,
      })
    )
  }

  const goToOverview = () =>
    history.push(
      replace(':id', loanApplicationId, routes.client.applications.overview.details)
    )
  const saveAndExit = (formikValues, applicantId) => {
    const formValues = formikValues?.financials[applicantPathId]
    dispatch(createExpensesAndLiabilities({ formValues, applicantId }))
    dispatch(resetForm())
    goToOverview()
  }

  const { previous, currentStep } = useWizard()
  const goBack = () => previous()

  const currentStepProps = ['expenses', 'liabilities', 'futurePlan']
  const canNext = hasNoErrors(currentStepProps)(errors?.financials?.[applicantPathId])

  const currentStepStatus = useSelector(
    selectProgressStepStatus(EXPENSES_AND_LIABILITIES, applicantId)
  )
  useEffect(() => {
    if (currentStep?.id === stepId)
      markProgressStepAsVisited(
        EXPENSES_AND_LIABILITIES,
        currentStepStatus,
        applicantId
      )
  }, [applicantId, currentStep, currentStepStatus, stepId])

  return (
    <Wizard.Step
      id={stepId}
      title="Your financials"
      validationSchema={useMemo(() => validationSchema(applicantPathId), [
        applicantPathId,
      ])}
      onSubmit={useCallback(saveAndContinue(applicantId), [applicantId])}
    >
      <Root>
        <ProgressContainer>
          <FormProgress saveAndExit={() => saveAndExit(formikValues, applicantId)} />
        </ProgressContainer>

        <Core>
          <Content>
            <FormContainer>
              <Title style={s('mb-5')}>Let&apos;s talk financials</Title>
              <SectionTitle style={s('mt-0 mb-4')}>Next, your expenses</SectionTitle>
              <Subtitle style={s('mb-6')}>
                So we can get a better idea of your financial position, please tell us
                the expenses that apply to your household on a monthly basis. Estimates
                are fine, but the more accurate you can be, the better.
              </Subtitle>

              <Expenses parentPath={parentPath} applicantPathId={applicantPathId} />

              <PersonalLoan parentPath={parentPath} applicantPathId={applicantPathId} />

              <CreditCard parentPath={parentPath} applicantPathId={applicantPathId} />

              <FuturePlan parentPath={parentPath} applicantPathId={applicantPathId} />
            </FormContainer>

            <InfoContainer>
              <InfoSidebar />
            </InfoContainer>
          </Content>

          <NavigationContainer>
            <BottomNavigation goBack={goBack} canNext={canNext} />
          </NavigationContainer>
        </Core>
      </Root>
    </Wizard.Step>
  )
}

export { YourExpensesAndLiabilitiesStep }
